import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Icon, ProgressBar, Spinner} from "@blueprintjs/core";

import {URL_API, URL_FILES} from "./data";
import "./StpFileStatus.scss";
import {toaster} from "./viewer/toaster";
import {processFileViaStreamingRequest} from "./processFileViaStreamingRequest";
import {StlFileItem} from "./StlFileItem";
import {Part} from "./types";

/**
 * Uploads an STP file, shows the processing status and the resulting STL parts
 * which can be interacted with based on the corresponding event handlers
 */
export const StpFileStatus: React.FC<{
  file: File;
  activeStlUrl?: string;
  view: (stlUrl: string) => void;
  reset: () => void;
}> = ({file, activeStlUrl, view, reset}) => {

  const started = useRef<boolean>(false);

  const [expectedParts, setExpectedParts] = useState<number | null>(null);
  const [parts, setParts]                 = useState<Part[]>([]);
  const [uploaded, setUploaded]           = useState<boolean>(false);
  const [, setLog]                        = useState<string[]>([]);

  const addPart = useCallback((part: Part) => {
    setParts(parts => [...parts, part]);
  }, [setParts]);

  const addLogLine = useCallback((line: string) => {
    setLog(lines => [...lines, line]);
  }, [setLog]);

  // TODO extract into dedicated hook
  useEffect(() => {

    if (started.current) return;

    started.current = true;

    processFileViaStreamingRequest(`${URL_API}explode`, file, line => {

      let matches;

      if (line === 'upload complete') setUploaded(true);

      if (line === 'from cache') toaster.show({
        icon:    'history',
        message: 'Retrieved from cache',
      });

      matches = /^found: (\d+) parts$/.exec(line);
      if (matches) {
        setExpectedParts(Number(matches[1]));
        return;
      }

      matches = /wrote: (\S+) bytes: (\d+) dimensions: ([.0-9a-z ]+)/.exec(line);
      if (matches) {
        addPart({
          path:       matches[1],
          size:       Number(matches[2]),
          dimensions: matches[3],
        });
      } else addLogLine(line);
    });
  }, [file, setUploaded, addLogLine, addPart]);

  const working = !uploaded || !expectedParts || parts.length < expectedParts;

  const complete = parts.length / (expectedParts ?? 1);

  return <div className={'stp-file-status'}>

    <div className={'upload-status'}>
      {(working) ? <Spinner size={16}/> : <Icon icon={'tick-circle'} intent={'success'}/>}
      {(() => {
        if (!uploaded) return 'Uploading...';
        if (!expectedParts) return 'Processing .STP file...';
        if (parts.length < expectedParts) {
          return `Processing part ${parts.length + 1} of ${expectedParts}...`;
        }
        return `Processed ${parts.length} parts`;
      })()}
    </div>

    <div className={'stp-file-progress-bar-wrapper'}>
      <ProgressBar
        animate={false}
        stripes={false}
        value={parts.length / (expectedParts ?? 1)}
        intent={complete ? 'success' : 'primary'}
      />
    </div>

    {parts.length ? <div className={'stl-file-list'}>
      {parts.map(part => {
        const url = URL_FILES + part.path;
        return <StlFileItem
          key={part.path}
          url={url}
          view={() => view(url)}
          active={activeStlUrl === url}
          part={part}
        />;
      })}
    </div> : null}

    {complete ? <Button onClick={reset} icon={'reset'} style={{margin: '10px 0 10px -6px'}} size="small" variant="minimal">
      Reset
    </Button> : null}

  </div>;
};